import React, { useState, useEffect } from "react";
import { Row, Col, Form, Button, ProgressBar, Alert, Modal } from "react-bootstrap";
import { FaUpload, FaFile, FaTrash, FaCheck } from "react-icons/fa";
import { Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import parse from 'html-react-parser';
import '../../css/fillInBlanks.scss';
import * as actions from "Store/Course/Instructor/OtherLabs/Actions";
import GradingDropdown from "../../GradeSubmission/GradingDropdown";
import httpService from 'services/httpService.js';
import * as constants from "Constants";
import displayGlobalMessage from "Utils/DisplayGlobalMessage";
import DocumentView from 'Screens1/Student/Course/Content/Multimedia/Components/DocumentView/DocumentView';
import AnswerExplanation from "../../GradeSubmission/AnswerExplaination";
import { removeRemarks } from "../../GradeSubmission/GradeCheck";
import { useSelector } from "react-redux";
import log from "loglevel";
import CryptoJS from 'crypto-js';

const toRelativeFilePath = (rawUrl) => {
  if (!rawUrl) return '';
  try {
    const parsed = new URL(rawUrl, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch (err) {
    return rawUrl;
  }
};

const CHUNK_SIZE = 512 * 1024; // 512KB chunks
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];

// Compute SHA256 hash of a chunk (Blob) for integrity verification
const computeChunkHash = (chunk) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const wordArray = CryptoJS.lib.WordArray.create(reader.result);
      const hash = CryptoJS.SHA256(wordArray).toString();
      resolve(hash);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(chunk);
  });
};

function FileUpload(props) {
  log.log("FileUpload component rendered", props);

  const { courseId, question, index, previousResponse, updateResponse, disable, Eval, showMarksToStudent } = props;
  const [questionResponseId, setQuestionResponseId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [fileError, setFileError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const userInfo = useSelector(state => state?.userInfo);
  const mode = userInfo?.mode;

  useEffect(() => {
    if (previousResponse && !questionResponseId) {
      setQuestionResponseId(previousResponse.id);
      if (previousResponse.response_text) {
        // Convert relative URL to absolute URL using backend base_url
        let fileUrl = previousResponse.response_text;
        log.log(`[FileUpload] Original URL: ${fileUrl}`);
        log.log(`[FileUpload] base_url: ${constants.base_url}`);
        if (fileUrl.startsWith('/media/')) {
          fileUrl = constants.base_url + fileUrl;
          log.log(`[FileUpload] Converted to absolute URL: ${fileUrl}`);
        }
        setUploadedFileUrl(fileUrl);
        // Extract filename from URL
        const urlParts = previousResponse.response_text.split('/');
        setUploadedFileName(urlParts[urlParts.length - 1]);
      }
    }
  }, [previousResponse]);

  const validateFile = (file) => {
    if (!file) {
      return "Please select a file";
    }

    // Check file type
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      return `Invalid file type. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`;
    }

    if (!ALLOWED_TYPES.includes(file.type) && file.type !== '') {
      return `Invalid file type. Allowed types: PDF, JPG, JPEG, PNG`;
    }

    return null;
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      setFileError(error);
      setSelectedFile(null);
      event.target.value = '';
      return;
    }

    setFileError("");
    setSelectedFile(file);
  };

  const uploadFileInChunks = async (file) => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

      log.log(`[FileUpload] Starting upload:`);
      log.log(`[FileUpload]   File name: ${file.name}`);
      log.log(`[FileUpload]   File size: ${file.size} bytes (${(file.size / 1024).toFixed(2)} KB)`);
      log.log(`[FileUpload]   CHUNK_SIZE: ${CHUNK_SIZE} bytes`);
      log.log(`[FileUpload]   Total chunks: ${totalChunks}`);

      // Phase 1: Send first chunk with metadata to create upload session
      const firstChunk = file.slice(0, CHUNK_SIZE);
      log.log(`[FileUpload] First chunk size: ${firstChunk.size} bytes`);

      // Compute hash for this chunk
      log.log(`[FileUpload] Computing hash for chunk 0...`);
      const firstChunkHash = await computeChunkHash(firstChunk);
      log.log(`[FileUpload] Chunk 0 hash: ${firstChunkHash}`);

      const firstFormData = new FormData();
      firstFormData.append('file', firstChunk);
      firstFormData.append('fileName', file.name);
      firstFormData.append('chunkHash', firstChunkHash);
      firstFormData.append('chunk_index', 0);
      firstFormData.append('total_chunks', totalChunks);
      firstFormData.append('questionId', question.id);
      firstFormData.append('questionResponseId', questionResponseId || '');

      // Get fileId from backend
      const firstResponse = await actions.uploadFileChunkRequest(
        question,
        firstFormData,
        questionResponseId
      );

      if (!firstResponse) {
        throw new Error('Failed to initiate file upload session');
      }

      // For single-chunk files, backend returns completed file_url immediately
      if (totalChunks === 1 && firstResponse.file_url) {
        let fileUrl = firstResponse.file_url || firstResponse.response_text;
        // Convert relative URL to absolute
        if (fileUrl && fileUrl.startsWith('/media/')) {
          fileUrl = constants.base_url + fileUrl;
        }
        setUploadedFileUrl(fileUrl);
        setUploadedFileName(file.name);
        setQuestionResponseId(firstResponse.id);

        // Update parent component
        const updatedResponse = {
          ...firstResponse,
          id: question?.id
        };
        log.log('[FileUpload] Calling updateResponse for single-chunk with:', updatedResponse);
        updateResponse(updatedResponse);

        displayGlobalMessage("File uploaded successfully", "success");
        setSelectedFile(null);
        document.getElementById(`file-input-${question.id}`).value = '';
        return; // Exit early for single-chunk uploads
      }

      if (!firstResponse.file_id) {
        throw new Error('Failed to initiate file upload session');
      }

      const fileId = firstResponse.file_id;
      setUploadProgress(Math.round((1 / totalChunks) * 100));

      // Phase 2: Upload remaining chunks using server-provided fileId
      for (let chunkIndex = 1; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        // Compute hash for this chunk
        log.log(`[FileUpload] Computing hash for chunk ${chunkIndex}...`);
        const chunkHash = await computeChunkHash(chunk);
        log.log(`[FileUpload] Chunk ${chunkIndex} hash: ${chunkHash}, size: ${chunk.size} bytes`);

        const formData = new FormData();
        formData.append('file', chunk);
        formData.append('fileId', fileId);
        formData.append('chunk_index', chunkIndex);
        formData.append('chunkHash', chunkHash);

        const response = await actions.uploadFileChunkRequest(
          question,
          formData,
          questionResponseId
        );

        // Update progress
        const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
        setUploadProgress(progress);

        // If this is the last chunk, save the file URL
        if (chunkIndex === totalChunks - 1 && response) {
          let fileUrl = response.file_url || response.response_text;
          // Convert relative URL to absolute
          if (fileUrl && fileUrl.startsWith('/media/')) {
            fileUrl = constants.base_url + fileUrl;
          }
          setUploadedFileUrl(fileUrl);
          setUploadedFileName(file.name);
          setQuestionResponseId(response.id);

          // Update parent component
          const updatedResponse = {
            ...response,
            id: question?.id
          };
          log.log('[FileUpload] Calling updateResponse for multi-chunk with:', updatedResponse);
          updateResponse(updatedResponse);
        }
      }

      displayGlobalMessage("File uploaded successfully", "success");
      setSelectedFile(null);
      // Reset file input
      document.getElementById(`file-input-${question.id}`).value = '';

    } catch (error) {
      log.error("File upload error:", error);
      displayGlobalMessage("File upload failed", "error");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      setFileError("Please select a file first");
      return;
    }
    uploadFileInChunks(selectedFile);
  };

  const handleRemoveFile = () => {
    setUploadedFileUrl("");
    setUploadedFileName("");
    setSelectedFile(null);
    setFileError("");
    // Also update the response to clear the file
    storeResponse("");
  };

  const resetFileInput = () => {
    const input = document.getElementById(`file-input-${question.id}`);
    if (input) {
      input.value = '';
    }
  };

  const handleDeleteUploadedFile = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      const result = await actions.deleteQuestionResponseRequest(question.id);
      if (result?.success) {
        setUploadedFileUrl("");
        setUploadedFileName("");
        setSelectedFile(null);
        setFileError("");
        setUploadProgress(0);
        setQuestionResponseId(null);
        resetFileInput();
        const clearedResponse = { id: question?.id, response_text: null, file_url: null };
        updateResponse(clearedResponse);
      }
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const storeResponse = async (fileUrl) => {
    if (!disable) {
      const response = await actions.addQuestionResponsesRequest(
        courseId,
        question,
        null,
        fileUrl,
        questionResponseId,
        null
      );
      if (response) {
        setQuestionResponseId(response.id);
        const updatedResponse = { ...response, id: question?.id };
        updateResponse(updatedResponse);
      }
    }
  };

  const handleSave = async (data) => {
    log.log('Grading Data:', data);
    if (data.marks > question?.marks) {
      displayGlobalMessage(`Question Grading failed: Invalid input: marks can't exceed ${question?.marks}`, 'error');
      return;
    } else {
      let url = constants.base_url + "/lab/activity/";
      let loc = location.pathname;
      let urlArray = loc.split('/');
      let user = userInfo.name || userInfo.username;
      let grade_data = {
        ...data,
        graded_by: user,
        question: question?.id,
        student_id: urlArray[urlArray.length - 1],
        otherlab: question?.otherlab,
        graded: true
      };
      log.log('graded data', grade_data);
      url = url + urlArray[urlArray.length - 3] + "?type=other_type_grade_submission";

      try {
        const response = await httpService.put(url, grade_data);
        log.log('response data:', response);
        if (response.status === 200) {
          displayGlobalMessage('Question Graded Successfully', 'success');
        } else {
          displayGlobalMessage('Question Grading failed', 'error');
        }
      } catch (error) {
        displayGlobalMessage('Question Grading failed', 'error');
        log.error(error);
      } finally {
        location.reload();
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const q_text = removeRemarks(question?.question_text);

  return (
    <Row className="fill-blanks-component" style={{ margin: 10 }}>
      <Col xs={12} style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px', borderBottom: '1px solid #ddd' }}>
          <b><i><span style={{ fontSize: 20, color: 'rgb(41, 121, 255)' }}>{`Question ${index + 1}`}</span></i></b>
          <span style={{ fontStyle: 'italic', fontWeight: 'bold', color: 'rgb(41, 121, 255)' }}>
            <i>Question Type: File Upload <span style={{ marginLeft: '40px' }}>Marks: {question.marks}{question.negative_marks ? `, Negative: ${question.negative_marks % 1 === 0 ? Math.floor(question.negative_marks) : question.negative_marks}` : ''}</span></i>
          </span>
        </div>
      </Col>

      <Col xs={12}>
        <b>{parse(`<p style={{ fontSize: '80px' }}>${q_text}</p>`)}</b>
      </Col>

      <Col xs={12} style={{ marginTop: 20 }}>
        {!disable && !uploadedFileUrl && (
          <div style={{ border: '2px dashed #ccc', borderRadius: '5px', padding: '20px', backgroundColor: '#f9f9f9' }}>
            <Form.Group>
              <Form.Label style={{ fontWeight: 'bold' }}>
                Upload File (Allowed: PDF, JPG, JPEG, PNG)
              </Form.Label>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
                <Form.Control
                  id={`file-input-${question.id}`}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileSelect}
                  disabled={disable || isUploading}
                  style={{ flex: 1 }}
                />
                <Button
                  variant="primary"
                  onClick={handleUpload}
                  disabled={!selectedFile || isUploading || disable}
                  style={{ minWidth: '100px' }}
                >
                  <FaUpload style={{ marginRight: '5px' }} />
                  {isUploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>

              {selectedFile && (
                <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                  <FaFile style={{ marginRight: '5px' }} />
                  Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </div>
              )}

              {fileError && (
                <Alert variant="danger" style={{ marginTop: '10px', marginBottom: 0 }}>
                  {fileError}
                </Alert>
              )}

              {isUploading && (
                <div style={{ marginTop: '10px' }}>
                  <ProgressBar
                    now={uploadProgress}
                    label={`${uploadProgress}%`}
                    animated
                    striped
                  />
                </div>
              )}
            </Form.Group>
          </div>
        )}

        {uploadedFileUrl && (
          <div style={{
            marginTop: '15px',
            padding: '15px',
            border: '2px solid #28a745',
            borderRadius: '5px',
            backgroundColor: '#d4edda'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
              <div>
                <FaCheck style={{ color: '#28a745', marginRight: '10px' }} />
                {mode === "I" ? (
                  <>
                    <strong>Uploaded File:</strong>{' '}
                    <button
                      type="button"
                      onClick={() => setPreviewFile({ url: uploadedFileUrl, name: uploadedFileName })}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: '#007bff',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        font: 'inherit',
                      }}
                    >
                      {uploadedFileName}
                    </button>
                  </>
                ) : (
                  <>
                    <strong>File Uploaded:</strong>{' '}
                    <span style={{ color: '#28a745', fontWeight: '500' }}>
                      {uploadedFileName}
                    </span>
                  </>
                )}
              </div>
              {mode !== "I" && !disable && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeleting || isUploading}
                >
                  <FaTrash style={{ marginRight: '5px' }} />
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              )}
            </div>
          </div>
        )}
      </Col>

      <Modal
        show={showDeleteConfirm}
        onHide={() => !isDeleting && setShowDeleteConfirm(false)}
        centered
        backdrop="static"
        keyboard={!isDeleting}
      >
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title>Delete Uploaded File</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{uploadedFileName}</strong>? You will be able to upload a new file in its place.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowDeleteConfirm(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteUploadedFile}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Dialog
        open={Boolean(previewFile)}
        onClose={() => setPreviewFile(null)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            paddingRight: '12px',
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 600, color: '#1a1a2e', wordBreak: 'break-all' }}>
            {previewFile?.name || ''}
          </span>
          <IconButton
            aria-label="Close preview"
            onClick={() => setPreviewFile(null)}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ overflow: 'hidden', padding: '16px', background: '#fafafa' }}
        >
          {previewFile && (
            <DocumentView
              selectedSubSection={{
                data: {
                  document_file: toRelativeFilePath(previewFile.url),
                  title: previewFile.name,
                  description: '',
                },
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {(disable === true && showMarksToStudent !== false) ? (
        <>
          <Col xs={6}>
            {mode !== "I" && question?.questionresponse_set?.graded && (() => {
              const qr = question.questionresponse_set;
              const gradedBy = qr.graded_by && qr.graded_by !== 'NA' && qr.graded_by !== 'None' ? qr.graded_by : '-';
              const gradedOn = qr.graded_on ? new Intl.DateTimeFormat('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }).format(new Date(qr.graded_on)).replace(/\s?(am|pm)\b/i, (m, p) => ' ' + p.toUpperCase()) : '-';
              const remark = qr.remark && qr.remark !== 'None' ? qr.remark : '';
              return (
                <div style={{ marginTop: 28, padding: '10px 14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13, color: '#374151' }}>
                  {remark && (
                    <div><strong>Remark:</strong> {remark}</div>
                  )}
                  <div style={{ marginTop: remark ? 4 : 0 }}>
                    <strong>Graded by:</strong> {gradedBy}
                    {qr.graded_on && <> ({gradedOn})</>}
                  </div>
                </div>
              );
            })()}
          </Col>
          <Col xs={6}>
            <Form.Check>
              <Form.Label>Scored Marks</Form.Label>
              <Form.Control
                type="number"
                min='0'
                value={(Eval || mode === "I" || question?.questionresponse_set?.graded) ? question?.questionresponse_set?.scored_marks : 0}
                step='0.1'
                disabled
              />
            </Form.Check>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              {mode === "I" && (
                <div style={{ marginRight: '-100px', marginTop: '-40px', marginLeft: '-250px' }}>
                  <GradingDropdown onSave={handleSave} />
                </div>
              )}
              {mode === "I" && (
                <div style={{ marginRight: '-100px', marginTop: '-40px', marginLeft: '-450px' }}>
                  <AnswerExplanation explanation={question?.answer_explaination} />
                </div>
              )}
            </div>
          </Col>
        </>
      ) : (
        <></>
      )}
    </Row>
  );
}

export default FileUpload;
